import { useLingui } from '@lingui/react/macro'
import { Button } from 'designSystem/components/Button'
import { Toggle } from 'designSystem/components/Toggle'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import { DOC_CHAT_FEEDBACK, useGeckoAnalytics } from 'features/analytics'
import {
  EvalCaseSaveError,
  useSaveEvalCase,
} from 'features/evaluations/agentic/postEvalCase'
import { AGENTIC_EVALUATIONS_CURATOR_PERMISSIONS } from 'features/evaluations/perms/evaluationsPermissions'
import { DocChatEvalCaseReview } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/DocChatEvalCaseReview'
import { DocChatFeedbackButton } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/DocChatFeedbackButton'
import type { PreviewDocumentHandler } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/search/previewDocumentHandler'
import { useAdvancedSearchUX } from 'features/knowledgeDocumentViewer/components/advancedSearch/contexts/AdvancedSearchUXContext'
import { useDocChatFeedbackBase } from 'features/knowledgeDocumentViewer/components/advancedSearch/hooks/useDocChatFeedbackBase'
import {
  buildDocChatEvalCase,
  type DocChatDraftCitation,
  docChatEvalCaseName,
  type DocChatResponseCitations,
  draftCitationKey,
  draftCitationsFromResponse,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/buildDocChatEvalCase'
import type { DocChatFeedbackReason } from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/docChatFeedback'
import { useKnowledgeDocumentDataContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentDataContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useGeckoPermissions } from 'features/permissions/hooks/useGeckoPerms'
import {
  BookmarkPlus,
  CircleCheck,
  Copy,
  RotateCcw,
  ThumbsUp,
} from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: -0.25rem;
  color: var(--muted-foreground);
`

const CopiedCheckIcon = styled(CircleCheck)`
  color: ${(props) => props.theme.colors.success['400_base']};
`

const COPIED_RESET_MS = 2000

type DocChatMessageActionsProps = {
  copyText: string
  retryQuery: string
  canSaveEvalCase?: boolean
  conversationId?: string | null
  turnId?: string
  response?: DocChatResponseCitations
  onPreviewDocument?: PreviewDocumentHandler
}

export function DocChatMessageActions({
  copyText,
  retryQuery,
  canSaveEvalCase = false,
  conversationId,
  turnId,
  response,
  onPreviewDocument,
}: DocChatMessageActionsProps): React.ReactElement {
  const { t } = useLingui()
  const { siteId, onSuggestionSelect } = useAdvancedSearchUX()
  const { trackEvent } = useGeckoAnalytics()
  const { hasPermissions, user } = useGeckoPermissions()
  const { orgID } = useKnowledgeDocumentDataContext()
  const { selectedPipelineConfigId } = usePipelineConfig()
  const saveEvalCase = useSaveEvalCase()
  const [rating, setRating] = useState<'up' | 'down' | null>(null)
  const [copied, setCopied] = useState(false)
  const [evalSaved, setEvalSaved] = useState(false)
  const [evalSaveError, setEvalSaveError] = useState<string | null>(null)
  const [evalSaveBusy, setEvalSaveBusy] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)
  const [caseName, setCaseName] = useState('')
  const [answerDraft, setAnswerDraft] = useState('')
  const [mustState, setMustState] = useState<string[]>([])
  const [mustNotState, setMustNotState] = useState<string[]>([])
  const [draftCitations, setDraftCitations] = useState<DocChatDraftCitation[]>(
    []
  )
  const copiedResetTimeoutRef = useRef<number | null>(null)
  const evalCaseNameIdRef = useRef<string | null>(null)
  const draftReadyRef = useRef(false)
  const canAuthorEvalCase = hasPermissions(
    AGENTIC_EVALUATIONS_CURATOR_PERMISSIONS
  )

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current !== null) {
        window.clearTimeout(copiedResetTimeoutRef.current)
      }
    }
  }, [])

  const handleCopy = useCallback(async () => {
    const text = copyText.trim()
    if (!text) return
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      if (copiedResetTimeoutRef.current !== null) {
        window.clearTimeout(copiedResetTimeoutRef.current)
      }
      copiedResetTimeoutRef.current = window.setTimeout(() => {
        setCopied(false)
        copiedResetTimeoutRef.current = null
      }, COPIED_RESET_MS)
    } catch {
      setCopied(false)
    }
  }, [copyText])

  const handleRetry = useCallback(() => {
    const trimmed = retryQuery.trim()
    if (!trimmed) return
    onSuggestionSelect(trimmed, {
      clearInputAfterSubmit: true,
      startDocChatTurn: true,
    })
  }, [onSuggestionSelect, retryQuery])

  const handleHelpful = useCallback(() => {
    if (rating === 'up') {
      setRating(null)
      return
    }
    setRating('up')
    trackEvent(DOC_CHAT_FEEDBACK.title, {
      rating: 'up',
      site_id: siteId,
      search_query: retryQuery,
    })
  }, [rating, retryQuery, siteId, trackEvent])

  const handleNotHelpful = useCallback(
    (reasons: DocChatFeedbackReason[], hasComment: boolean) => {
      setRating('down')
      trackEvent(DOC_CHAT_FEEDBACK.title, {
        rating: 'down',
        site_id: siteId,
        search_query: retryQuery,
        reasons,
        has_comment: hasComment,
      })
    },
    [retryQuery, siteId, trackEvent]
  )

  const clearRating = useCallback(() => setRating(null), [])

  const ensureCaseNameId = useCallback(() => {
    evalCaseNameIdRef.current ??= crypto
      .randomUUID()
      .replace(/-/g, '')
      .slice(0, 8)
    return evalCaseNameIdRef.current
  }, [])

  const toggleReview = useCallback(() => {
    if (evalSaved || evalSaveBusy) return
    setEvalSaveError(null)
    if (!reviewOpen && !draftReadyRef.current) {
      draftReadyRef.current = true
      setAnswerDraft(copyText.trim())
      setCaseName(docChatEvalCaseName(retryQuery.trim(), ensureCaseNameId()))
      setDraftCitations(draftCitationsFromResponse(response))
    }
    setReviewOpen((open) => !open)
  }, [
    copyText,
    ensureCaseNameId,
    evalSaveBusy,
    evalSaved,
    response,
    retryQuery,
    reviewOpen,
  ])

  const addCitation = useCallback(
    (citation: DocChatDraftCitation) => {
      const key = draftCitationKey(citation, selectedPipelineConfigId)
      setDraftCitations((current) =>
        current.some(
          (item) => draftCitationKey(item, selectedPipelineConfigId) === key
        )
          ? current
          : [...current, citation]
      )
    },
    [selectedPipelineConfigId]
  )

  const removeCitation = useCallback(
    (citation: DocChatDraftCitation) => {
      const key = draftCitationKey(citation, selectedPipelineConfigId)
      setDraftCitations((current) =>
        current.filter(
          (item) => draftCitationKey(item, selectedPipelineConfigId) !== key
        )
      )
    },
    [selectedPipelineConfigId]
  )

  const updatedBy = user?.email?.trim() ?? ''
  const { sessionId, canGiveFeedback, feedback } = useDocChatFeedbackBase({
    conversationId,
    turnId,
    response,
  })
  const showSaveEvalCase =
    canSaveEvalCase &&
    canAuthorEvalCase &&
    Boolean(copyText.trim()) &&
    Boolean(retryQuery.trim()) &&
    Boolean(orgID) &&
    Boolean(siteId) &&
    Boolean(updatedBy)

  const handleSaveEvalCase = useCallback(async () => {
    const query = retryQuery.trim()
    const replyText = answerDraft.trim()
    if (
      !showSaveEvalCase ||
      evalSaved ||
      evalSaveBusy ||
      !caseName.trim() ||
      !replyText
    ) {
      return
    }
    setEvalSaveBusy(true)
    setEvalSaveError(null)
    try {
      await saveEvalCase(
        buildDocChatEvalCase({
          query,
          replyText,
          siteId,
          orgId: orgID,
          conversationId: sessionId,
          citedPages: draftCitations,
          pipelineConfigId: selectedPipelineConfigId,
          mustState,
          mustNotState,
          updatedBy,
          capturedAt: new Date().toISOString(),
          nameId: ensureCaseNameId(),
          name: caseName,
        })
      )
      setEvalSaved(true)
      setReviewOpen(false)
    } catch (cause) {
      setEvalSaveError(
        cause instanceof EvalCaseSaveError
          ? cause.detail
          : cause instanceof Error
            ? cause.message
            : t`Could not save the eval case`
      )
    } finally {
      setEvalSaveBusy(false)
    }
  }, [
    answerDraft,
    caseName,
    draftCitations,
    ensureCaseNameId,
    evalSaveBusy,
    evalSaved,
    mustNotState,
    mustState,
    orgID,
    retryQuery,
    saveEvalCase,
    selectedPipelineConfigId,
    sessionId,
    showSaveEvalCase,
    siteId,
    t,
    updatedBy,
  ])

  const canCopy = copyText.trim().length > 0
  const canRetry = retryQuery.trim().length > 0
  const saveEvalLabel = evalSaved
    ? t`Saved as eval case`
    : evalSaveError
      ? evalSaveError
      : t`Save as eval case`

  return (
    <>
      <ActionsRow>
        <Tooltip open={copied ? true : undefined}>
          <TooltipTrigger asChild>
            <Button
              type='button'
              variant='ghost'
              size='icon'
              aria-label={copied ? t`Copied` : t`Copy response`}
              disabled={!canCopy}
              onClick={handleCopy}>
              {copied ? <CopiedCheckIcon /> : <Copy />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side='top'>
            {copied ? t`Copied` : t`Copy`}
          </TooltipContent>
        </Tooltip>
        <Toggle
          aria-label={t`Helpful`}
          title={t`Helpful`}
          pressed={rating === 'up'}
          onPressedChange={handleHelpful}>
          <ThumbsUp
            size={14}
            fill={rating === 'up' ? 'currentColor' : 'none'}
          />
        </Toggle>
        <DocChatFeedbackButton
          rated={rating === 'down'}
          disabled={!canGiveFeedback}
          feedback={feedback}
          onRated={handleNotHelpful}
          onCleared={clearRating}
        />
        <Button
          type='button'
          variant='ghost'
          size='icon'
          aria-label={t`Retry search`}
          title={t`Retry`}
          disabled={!canRetry}
          onClick={handleRetry}>
          <RotateCcw />
        </Button>
        {showSaveEvalCase ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                aria-label={saveEvalLabel}
                aria-expanded={reviewOpen}
                disabled={evalSaved || evalSaveBusy}
                onClick={toggleReview}>
                {evalSaved ? <CopiedCheckIcon /> : <BookmarkPlus />}
              </Button>
            </TooltipTrigger>
            <TooltipContent side='top'>{saveEvalLabel}</TooltipContent>
          </Tooltip>
        ) : null}
      </ActionsRow>
      {showSaveEvalCase && reviewOpen ? (
        <DocChatEvalCaseReview
          caseName={caseName}
          query={retryQuery.trim()}
          answer={answerDraft}
          siteId={siteId}
          orgId={orgID}
          capturedBy={updatedBy}
          sessionId={sessionId}
          citations={draftCitations}
          mustState={mustState}
          mustNotState={mustNotState}
          onCaseNameChange={setCaseName}
          onAnswerChange={setAnswerDraft}
          onMustStateChange={setMustState}
          onMustNotStateChange={setMustNotState}
          response={response}
          onAddCitation={addCitation}
          onRemoveCitation={removeCitation}
          onPreviewDocument={onPreviewDocument}
          busy={evalSaveBusy}
          error={evalSaveError}
          onCancel={() => setReviewOpen(false)}
          onSave={() => {
            void handleSaveEvalCase()
          }}
        />
      ) : null}
    </>
  )
}
